import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X, ExternalLink, ArrowLeft } from 'lucide-react'
import { News } from '../types'
import { newsApi } from '../lib/api'
import NewsImage from '../components/NewsImage'
import { useImageCache } from '../hooks/useImageCache'
import { getCache, getCacheStale, setCache } from '../lib/cache'

const NewsPage = () => {
  const navigate = useNavigate()
  const { preloadImages } = useImageCache()
  const [searchParams] = useSearchParams()
  const newsId = searchParams.get('id')
  
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNewsIndex, setSelectedNewsIndex] = useState(0)
  const selectedNewsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadNews()
  }, [])

  // Pre-cargar todas las imágenes cuando se cargan las noticias
  useEffect(() => {
    if (news.length > 0) {
      const imageKeys = news.map(n => n.image_url ?? null).filter((key): key is string | null => key !== undefined)
      preloadImages(imageKeys)
    }
  }, [news, preloadImages])

  useEffect(() => {
    if (newsId && news.length > 0) {
      const index = news.findIndex(n => n.id === newsId)
      if (index !== -1) {
        setSelectedNewsIndex(index)
      }
    }
  }, [newsId, news])

  // Scroll to selected news when index changes
  useEffect(() => {
    if (selectedNewsRef.current) {
      setTimeout(() => {
        selectedNewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [selectedNewsIndex])

  const loadNews = async () => {
    const cacheKey = 'customer_news_all'
    
    // Intentar cargar desde cache primero
    const cachedNews = getCacheStale<News[]>(cacheKey)
    if (cachedNews && cachedNews.length > 0) {
      setNews(cachedNews)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await newsApi.getNews(50) // Cargar más noticias para la página
      if (response.success) {
        const newsData = response.data || []
        setNews(newsData)
        
        // Guardar en cache (10 minutos de TTL - noticias cambian moderadamente)
        setCache(cacheKey, newsData, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading news:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedNews) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    } finally {
      setLoading(false)
    }
  }

  const handleNewsClick = (index: number) => {
    setSelectedNewsIndex(index)
    const selectedNews = news[index]
    if (selectedNews) {
      navigate(`/news?id=${selectedNews.id}`, { replace: true })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Cargando noticias...</p>
        </div>
      </div>
    )
  }

  if (news.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No hay noticias disponibles</p>
          </div>
        </div>
      </div>
    )
  }

  const currentNews = news[selectedNewsIndex]

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <h1 className="text-4xl font-bold text-gray-900">Noticias</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main News Content */}
          <div className="lg:col-span-2">
            {currentNews && (
              <div ref={selectedNewsRef} className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden">
                {/* News Image */}
                {currentNews.image_url && (
                  <div className="w-full h-96 bg-gray-100">
                    <NewsImage
                      imageKey={currentNews.image_url}
                      className="w-full h-full object-cover"
                      alt={currentNews.title}
                    />
                  </div>
                )}

                {/* News Content */}
                <div className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {currentNews.title}
                  </h2>
                  <div className="prose prose-lg max-w-none mb-6">
                    <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                      {currentNews.content}
                    </p>
                  </div>
                  
                  {currentNews.link_url && (
                    <a
                      href={currentNews.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all"
                    >
                      {currentNews.link_text || 'Ver enlace externo'}
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}

                  {/* News Metadata */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      {currentNews.created_at && new Date(currentNews.created_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* All News Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Todas las noticias</h3>
              <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                {news.map((newsItem, index) => (
                  <div
                    key={newsItem.id}
                    onClick={() => handleNewsClick(index)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      index === selectedNewsIndex
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {newsItem.image_url && (
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <NewsImage
                            imageKey={newsItem.image_url}
                            className="w-full h-full object-cover"
                            alt={newsItem.title}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-bold text-gray-900 mb-1 line-clamp-2">
                          {newsItem.title}
                        </h4>
                        <p className="text-gray-600 text-xs line-clamp-2">
                          {newsItem.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewsPage

